import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {GruppeId, GruppeInvitasjonTilGodkjenningModell, MedlemId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {InvitasjonerDatatjeneste} from "felles-ng2/tjenester/datalager/InvitasjonerDatatjeneste";
import {firstValueFrom, Observable} from "rxjs";
import {FeilhåndteringsstrategiForDataOgMeta} from "felles-ng2/tjenester/datalager/datatjeneste";
import {DataOgMeta, kunRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {blankGruppeInvitasjonTilGodkjenningModell} from "felles-ng2/modell/invitasjonModellHjelpere";
import {OtpSkjemaKomponent} from "web-ng2/delt/komponenter/skjema/otp-felt/otp-felt";
import {InvitasjonOtpSendtModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";

const debug = initDebugger('GruppeGodtaInvitasjonDialogKomponent');

export type GruppeGodtaInvitasjonDialogRespons = GruppeModellDML | null;

export interface DialogParametre {
    invitasjon: GruppeInvitasjonTilGodkjenningModell;
}

/**
 * Modal dialog der bruker kan godta invitasjon.
 */
@Component({
    selector: 'gruppe-godta-invitasjon-dialog',
    templateUrl: 'gruppe-godta-invitasjon-dialog.html',
    styleUrl: 'gruppe-godta-invitasjon-dialog.scss',
    imports: [
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        MatProgressSpinnerModule,
        TranslateModule,
        ReactiveFormsModule,
        OtpSkjemaKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeGodtaInvitasjonDialogKomponent {

    static vis(dialog: MatDialog, invitasjon: GruppeInvitasjonTilGodkjenningModell): MatDialogRef<GruppeGodtaInvitasjonDialogKomponent, GruppeGodtaInvitasjonDialogRespons> {
        return dialog.open(GruppeGodtaInvitasjonDialogKomponent, {data: {invitasjon} as DialogParametre, disableClose: true, ariaLabelledBy: 'gruppe-godta-invitasjon-dialog-tittel', width: '500px'});
    }

    invitasjon = signal(blankGruppeInvitasjonTilGodkjenningModell());
    steg = signal<'laster' | 'sendkode' | 'oppgikode' | 'kvittering'>('laster');
    otpSendtTil = signal<string>('');
    otpValideringFeilet = signal(false);
    lagrer = asynkArbeidPågårSignal();
    otpKodeSkjema = new FormControl('', {validators: Validators.required});

    /** Gruppen bruker har fått tilgang til når invitasjon har blitt godtatt */
    private nyGruppe: GruppeModellDML | null = null;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: GruppeGodtaInvitasjonDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private dialogRef: MatDialogRef<GruppeGodtaInvitasjonDialogKomponent, GruppeGodtaInvitasjonDialogRespons>) {

        //hent data, lever kun invitasjon hentet fra server
        firstValueFrom(this.avhengigheter.invitasjon(parametre.invitasjon.id)
          .pipe(kunRequestRespons()))
          .then(invitasjon => {
              if (!invitasjon.meta.httpResponsStatusOk) {
                  this.meldingTilBruker.feilmelding(this.oversettelser.oversett(i18n('Kunne ikke hente invitasjon')));
                  this.avbryt();
                  return;
              }
              this.invitasjon.set(invitasjon.data);
              this.gåTilInitieltSteg();
          });
    }

    async sendOtp() {
        await this.lagrer.utfør(async () => {
            const respons = await this.avhengigheter.sendOtp(this.invitasjon().id);
            this.otpSendtTil.set(respons.otpSmsSendtTil);
            this.steg.set('oppgikode');
        });
    }

    async godtaInvitasjon() {
        debug('godtaInvitasjon', this.otpKodeSkjema.value)
        await this.lagrer.utfør(async () => {
            const respons = await this.avhengigheter.godtaInvitasjon(this.invitasjon().id, this.otpKodeSkjema.value!);
            this.otpValideringFeilet.set(!respons.otpValidert);
            if (!this.otpValideringFeilet()) await this.invitasjonGodtatt(respons.gruppeId);
        });
    }

    async sendNyOtp() {
        await this.sendOtp();
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Ny engangskode er sendt til din mobiltelefon')));
    }

    lukk() {
        this.dialogRef.close(this.nyGruppe);
    }

    avbryt() {
        this.dialogRef.close(null);
    }

    //private hjelpemetoder

    private async gåTilInitieltSteg() {
        if (this.invitasjon().kreverOtpGodkjenning) {
            this.steg.set('sendkode');
            return;
        }
        await this.godtaInvitasjon();
    }

    private async invitasjonGodtatt(nyGruppeId: GruppeId) {
        this.steg.set('kvittering');
        this.nyGruppe = await this.avhengigheter.gruppe(nyGruppeId)
    }
}

@Injectable()
export class GruppeGodtaInvitasjonDialogKomponentAvhengigheter {

    constructor(private invitasjonerDatatjeneste: InvitasjonerDatatjeneste, private gruppeDatatjeneste: GruppeDatatjeneste, private kontekstAdministrasjon: BrukerkontekstAdministrasjon) {
    }

    gruppe(id: GruppeId): Promise<GruppeModellDML> {
        return firstValueFrom(this.gruppeDatatjeneste.hent(id));
    }

    invitasjon(id: MedlemId): Observable<DataOgMeta<GruppeInvitasjonTilGodkjenningModell>> {
        return this.invitasjonerDatatjeneste.hentMedMeta(id, FeilhåndteringsstrategiForDataOgMeta.propagerViaDataOgMeta);
    }

    sendOtp(id: MedlemId): Promise<InvitasjonOtpSendtModell> {
        return this.invitasjonerDatatjeneste.sendOtpTilInnloggetBruker(id);
    }

    async godtaInvitasjon(id: MedlemId, engangskode: string): Promise<GruppeInvitasjonTilGodkjenningModell> {
        const oppdatertInvitasjon = await this.invitasjonerDatatjeneste.godtaInvitasjonSomEksisterendeBruker(id, engangskode);
        //må relaste kontekst da bruker nå har tilgang til en ny gruppe
        this.kontekstAdministrasjon.relastKontekst();
        return oppdatertInvitasjon;
    }
}
